import { useState } from "react"
import { Typography } from "@octopus-energy/coral/atoms"
import { ZodFormik } from "@oefr/ui"
import { formatStreetName, formatStreetNumber } from "@oefr/utils/string"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import z from "zod"
import { FormActions } from "@/components/organisms"
import { onboardingDataLayer, OnboardingDataLayerEvent } from "@/features/onboarding/datalayer"
import { type OnboardingState } from "@/features/onboarding/wizard"
import { hasElectricity, hasGas, SubscriptionContainer } from "@/features/subscription"
import { EligibilityLoadingModal } from "@/features/subscription/meterEligibility"
import { graphql, type InputOf } from "@/graphql"
import { useGraphQLClient } from "@/lib/blueprint-auth/client"
import { OnboardingRouteNames } from "@/routes/onboarding"
import { JourneyType } from "@/scalars/journeyType"
import { useInitialOnboardingFormContext } from "../../context/InitialOnboardingFormContext"
import { useEligibilityCheckQuote } from "../../hooks/useEligibilityCheckQuote"
import { MeterPointIdForm } from "../MeterPointIdForm"
import { useMeterPointEligibilitySchemas } from "./MeterPointEligibility.schema"

const MeterPointNumberBySerialQuery = graphql(`
    query MeterPointNumberBySerial($input: MeterPointNumberByAddressInputType!) {
        meterPointNumberByAddress(input: $input)
    }
`)

export function MeterPointEligibility() {
    const t = useTranslations("MeterPointEligibility")
    const { electricitySchema, gasNormalSchema, gasSerialSchema } =
        useMeterPointEligibilitySchemas()

    const {
        selectedEnergyTypes,
        journeyType,
        onboardingState,
        getElectricityMeterPoint,
        getGasMeterPoint,
    } = useInitialOnboardingFormContext()
    const electricityMeterPoint = getElectricityMeterPoint()
    const gasMeterPoint = getGasMeterPoint()

    const [isGasSerialMode, setIsGasSerialMode] = useState(false)
    const [gasSerialPceNotFound, setGasSerialPceNotFound] = useState(false)

    const { error, validateMeterPoints } = useEligibilityCheckQuote({
        stepName: OnboardingRouteNames.MeterDetails,
    })

    const queryClient = useQueryClient()
    const graphQLClient = useGraphQLClient()

    const isValidationButtonDisabled =
        (electricityMeterPoint?.hasChangeOfSupplierError ||
            gasMeterPoint?.hasChangeOfSupplierError) &&
        journeyType === JourneyType.CHANGE_OF_SUPPLIER

    const Schema = z.object({
        electricity: hasElectricity(selectedEnergyTypes) ? electricitySchema : z.undefined(),
        gas: hasGas(selectedEnergyTypes)
            ? isGasSerialMode
                ? gasSerialSchema
                : gasNormalSchema
            : z.undefined(),
    })

    const getMeterPointBySerial = async (input: InputOf<typeof MeterPointNumberBySerialQuery>) => {
        try {
            const { meterPointNumberByAddress } = await queryClient.fetchQuery({
                queryKey: ["MeterPointNumberBySerialQuery", input],
                queryFn() {
                    return graphQLClient.request(MeterPointNumberBySerialQuery, { input })
                },
            })

            return meterPointNumberByAddress
        } catch {
            return null
        }
    }

    const handleSubmit = async (values: z.infer<typeof Schema>) => {
        const newOnboardingState: OnboardingState = {
            formValues: {
                ...structuredClone(onboardingState.formValues),
                journeyType,
                selectedEnergyTypes,
            },
            stepHistory: [OnboardingRouteNames.MeterDetails],
            submittedSteps: [],
        }

        onboardingDataLayer.sendTrackingEvent(
            OnboardingDataLayerEvent.MeterPointsSent,
            newOnboardingState.formValues
        )

        const meterPoints = []

        if (values.electricity) {
            meterPoints.push({
                ...values.electricity,
                marketName: "FRA_ELECTRICITY" as const,
                journeyType,
            })
        }

        if (values.gas) {
            if (isGasSerialMode) {
                const gasValues = values.gas as {
                    meterSerialNumber: string
                    address: { addrNumStreet: string; addrPostCode: string; addrCityLabel: string }
                    givenName: string
                    familyName: string
                }

                const meterPointId = await getMeterPointBySerial({
                    marketName: "FRA_GAS",
                    streetNumber: formatStreetNumber(gasValues.address.addrNumStreet),
                    streetName: formatStreetName(gasValues.address.addrNumStreet),
                    postcode: gasValues.address.addrPostCode,
                    city: gasValues.address.addrCityLabel.replaceAll("-", " ").toUpperCase(),
                    serialNumber: gasValues.meterSerialNumber,
                })

                if (!meterPointId) {
                    setGasSerialPceNotFound(true)
                    setIsGasSerialMode(false)

                    return
                }

                meterPoints.push({
                    meterPointId,
                    marketName: "FRA_GAS" as const,
                    journeyType,
                    givenName: gasValues.givenName,
                    familyName: gasValues.familyName,
                })
            } else {
                const { meterPointId, givenName, familyName } = values.gas as {
                    meterPointId: string
                    givenName: string
                    familyName: string
                }

                meterPoints.push({
                    meterPointId,
                    givenName,
                    familyName,
                    marketName: "FRA_GAS" as const,
                    journeyType,
                })
            }
        }

        await validateMeterPoints(meterPoints, newOnboardingState)
    }

    const initialValues = {
        electricity: hasElectricity(selectedEnergyTypes)
            ? {
                  meterPointId: electricityMeterPoint?.meterPointId || "",
                  givenName: electricityMeterPoint?.givenName || "",
                  familyName: electricityMeterPoint?.familyName || "",
              }
            : undefined,
        gas: hasGas(selectedEnergyTypes)
            ? {
                  meterPointId: gasMeterPoint?.meterPointId || "",
                  givenName: gasMeterPoint?.givenName || "",
                  familyName: gasMeterPoint?.familyName || "",
                  meterSerialNumber: "",
                  address: {
                      addrNumStreet: "",
                      addrPostCode: "",
                      addrCityLabel: "",
                      fullAddress: "",
                  },
              }
            : undefined,
    }

    return (
        <ZodFormik
            initialValues={initialValues}
            validationSchema={Schema}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({ isSubmitting }) => (
                <>
                    <EligibilityLoadingModal isLoading={isSubmitting} />
                    <SubscriptionContainer.Form gap="md">
                        {selectedEnergyTypes.map((energyType) => (
                            <MeterPointIdForm
                                key={energyType}
                                energyType={energyType}
                                {...(energyType === "gas"
                                    ? {
                                          isSerialMode: isGasSerialMode,
                                          onSwitchToSerial: () => {
                                              setGasSerialPceNotFound(false)
                                              setIsGasSerialMode(true)
                                          },
                                          onSwitchToPce: () => setIsGasSerialMode(false),
                                          serialPceNotFound: gasSerialPceNotFound,
                                      }
                                    : {})}
                            />
                        ))}
                        <FormActions step>
                            <FormActions.Error error={error} />
                            <FormActions.Accept disabled={isValidationButtonDisabled} />
                            <Typography textStyle="caption">
                                {t("legal", {
                                    energyType:
                                        selectedEnergyTypes.length > 1
                                            ? "dual"
                                            : selectedEnergyTypes[0],
                                })}
                            </Typography>
                        </FormActions>
                    </SubscriptionContainer.Form>
                </>
            )}
        </ZodFormik>
    )
}

MeterPointEligibility.messages = [
    "MeterPointEligibility",
    ...MeterPointIdForm.messages,
    ...FormActions.Error.messages,
    ...EligibilityLoadingModal.messages,
] as const
